const API_BASE = "https://irene-api.heysalad.app"

function getToken(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|; )irene_token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

class CanvaApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = "CanvaApiError"
    this.status = status
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }
  if (token) headers["Authorization"] = `Bearer ${token}`
  if (!(options.body instanceof FormData)) headers["Content-Type"] = "application/json"

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, mode: "cors" })
  const data = await res.json()
  if (!res.ok) throw new CanvaApiError(data.error || `Request failed (${res.status})`, res.status)
  return data as T
}

// ---------------------------------------------------------------------------
// OAuth
// ---------------------------------------------------------------------------

export async function getCanvaConnectionStatus() {
  const res = await request<{ success: boolean; connected: boolean }>("/canva/auth/status")
  return { connected: res.connected }
}

export async function getCanvaAuthUrl() {
  const res = await request<{ success: boolean; data: { authUrl: string; state: string } }>("/canva/auth/url")
  return res.data
}

export async function exchangeCanvaCode(code: string, state: string) {
  const res = await request<{ success: boolean; connected: boolean }>("/canva/auth/callback", {
    method: "POST",
    body: JSON.stringify({ code, state }),
  })
  return { connected: res.connected }
}

export async function disconnectCanva() {
  await request("/canva/auth/disconnect", { method: "DELETE" })
}

// ---------------------------------------------------------------------------
// Designs
// ---------------------------------------------------------------------------

export interface CanvaDesignItem {
  id: string
  canva_design_id: string
  title: string
  design_type: string
  edit_url: string | null
  view_url: string | null
  thumbnail_url: string | null
  deck_id: string | null
  investor_id: string | null
  deck_name?: string | null
  investor_name?: string | null
  page_count: number | null
  status: string
  created_at: string
  updated_at: string
}

export async function getCanvaDesigns() {
  const res = await request<{ success: boolean; designs: CanvaDesignItem[] }>("/canva/designs")
  return res.designs
}

export async function createCanvaDesign(title: string, investorId?: string) {
  const res = await request<{ success: boolean; design: CanvaDesignItem }>("/canva/designs", {
    method: "POST",
    body: JSON.stringify({ title, investorId }),
  })
  return res.design
}

export async function importDeckToCanva(deckId: string) {
  const res = await request<{ success: boolean; importJob: any; design?: CanvaDesignItem }>(
    `/canva/designs/import/${deckId}`,
    { method: "POST" }
  )
  return res
}

export async function getCanvaDesignDetails(designId: string) {
  const res = await request<{ success: boolean; design: CanvaDesignItem }>(`/canva/designs/${designId}`)
  return res.design
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export interface CanvaExportJobResult {
  id: string
  status: "in_progress" | "success" | "failed"
  urls?: string[]
  error?: { code: string; message: string }
}

export async function startCanvaExport(canvaDesignId: string, format: string) {
  const res = await request<{ success: boolean; job: CanvaExportJobResult }>("/canva/export", {
    method: "POST",
    body: JSON.stringify({ canvaDesignId, format }),
  })
  return res.job
}

export async function checkCanvaExport(jobId: string) {
  const res = await request<{ success: boolean; job: CanvaExportJobResult }>(`/canva/export/${jobId}`)
  return res.job
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

export async function uploadCanvaAsset(file: File) {
  const formData = new FormData()
  formData.append("file", file)

  const res = await request<{ success: boolean; assetId: string }>("/canva/assets", {
    method: "POST",
    body: formData,
  })
  return res.assetId
}
